import { isEqual } from 'lodash-es';
import moment from 'moment';
import React, { useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import { getFilterParams } from '~/src/dash/components/filters/common';
import { extractRouteParams, navigate, RouteChannelNew } from '~/src/dash/router/routes';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { checkUserInfoMasked, hasRoleSuperUser } from '~/src/dash/utils/roles';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import {
  deleteChannel,
  fetchChannels,
  IChannel,
  IChannelRow,
  IChannelsUiParams,
  IGetChannelsAPIParams,
  updateChannel,
} from '~/src/dash/views/channels/api';
import { Channels } from '~/src/dash/views/channels/Channels';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

export const ChannelsContainer = () => {
  const { session, user } = useBaseStore();
  const isSuperUser = hasRoleSuperUser(user.user);
  const cfg = useBaseConfig().Channels.All;

  const [channels, setChannels] = useState<IChannelRow[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [error, setError] = useState<any>(null);
  const [uiParams, setUiParams] = useState<IChannelsUiParams>({});
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [isActionPending, setIsActionPending] = useState(false);

  // format request params for onFetchChannels
  const formatParams = (params: IChannelsUiParams): IGetChannelsAPIParams => ({
    idSearch: parseInt(params.searchInput || '', 10) || '',
    nameSearch: params.searchInput,
    orderBy: params.orderBy,
    orderDirection: params.order,
    page: params.currPage,
    pageSize: params.rowsPerPage,
    preloads: ['ChannelLabels', 'SitesCronSummary'],
    uiFields: ['id', 'uuid', 'name', 'active', 'bid_floor', 'currency_displayed'],
    ...getFilterParams(params.filters || {}, cfg.filters),
  });

  // format onFetchChannels' response
  const formatChannels = (channels: IChannel[]): IChannelRow[] => {
    const channelRows: IChannelRow[] = [];
    channels.forEach((channel: IChannel) => {
      const { ID, Active, BidFloor, Name, UUID, ChannelLabels, SitesCronSummary } = channel;
      const channelRow: IChannelRow = {
        ID,
        UUID,
        Name: checkUserInfoMasked(user.user) ? `demo_channel_${ID}` : Name, // mask channel info if user is a demo account
        Active,
        BidFloor,
        ChannelLabels: ChannelLabels?.map((label) => label.Name).sort() || [],
        Last24HrAverageCPM: SitesCronSummary?.Last24HrAverageCPM || 0,
        Last24HrEndpointRequests: SitesCronSummary?.Last24HrEndpointRequests || 0,
        Last24HrImpressions: SitesCronSummary?.Last24HrImpressions || 0,
        Last24HrRevenue: SitesCronSummary?.Last24HrRevenue || 0,
        PastWeekAverageCPM: SitesCronSummary?.PastWeekAverageCPM || 0,
        PastWeekEndpointRequests: SitesCronSummary?.PastWeekEndpointRequests || 0,
        PastWeekImpressions: SitesCronSummary?.PastWeekImpressions || 0,
        PastWeekRevenue: SitesCronSummary?.PastWeekRevenue || 0,
        PastWeekImpressionsTrend: SitesCronSummary?.PastWeekImpressionsTrend,
        PastWeekRevenueTrend: SitesCronSummary?.PastWeekRevenueTrend,
        ExchangeRate: channel.ExchangeRate,
      };
      channelRows.push(channelRow);
    });
    return channelRows;
  };

  // fetch a list of channels
  const onFetchChannels = (params: IChannelsUiParams) => {
    const startTime = moment().unix();
    setLoadState(LoadingStateLoading);
    // only update if params has changed
    if (!isEqual(uiParams, params)) {
      updateCfg(params);
      setUiParams(params);
    }
    fetchChannels(session.publisher.ID, formatParams(params))
      .then(({ Channels, TotalRows }) => {
        setChannels(formatChannels(Channels));
        setTotalRows(TotalRows);
        setLoadState(LoadingStateHasData);
        const loadTime = moment().unix() - startTime;
        track(
          'Channels - All - List Loaded',
          {
            isSuperUser,
            loadTime,
            numRowsDisplayed: Channels.length,
            totalRowCount: TotalRows,
          },
          user.user,
        );
      })
      .catch((err) => {
        setError(err);
        setSnackbarMessage('Error retrieving data.');
        setLoadState(LoadingStateNoData);
      });
  };

  // delete one or many channels
  const onDeleteChannels = (channelIDs: string[]) => {
    const isBulkDelete = channelIDs.length > 1;
    setIsActionPending(true);
    return Promise.all(
      channelIDs.map((channelID) => deleteChannel(session.publisher.ID, channelID)),
    )
      .then(() => {
        track(
          `Channels - All - ${isBulkDelete ? 'Bulk Delete' : 'Delete Channel'}`,
          { isSuperUser },
          user.user,
        );
        unstable_batchedUpdates(() => {
          setIsActionPending(false);
          setSnackbarMessage(
            `Successfully deleted ${
              isBulkDelete ? `${channelIDs.length} channels` : `channel [${channelIDs[0]}]`
            }.`,
          );
        });
        onFetchChannels(uiParams);
      })
      .catch((err) => {
        unstable_batchedUpdates(() => {
          setIsActionPending(false);
          setSnackbarMessage(
            `Error deleting ${
              isBulkDelete ? `${channelIDs.length} channels` : `channel [${channelIDs[0]}]`
            }.`,
          );
          setError(err);
        });
      });
  };

  // update one or many channels
  const onUpdateChannels = (channelIDs: string[], updates: Partial<IChannelRow>) => {
    const isBulkUpdate = channelIDs.length > 1;
    setIsActionPending(true);
    Promise.all(
      channelIDs.map((channelID) => updateChannel(session.publisher.ID, channelID, updates)),
    )
      .then(() => {
        track(
          `Channels - All - ${isBulkUpdate ? 'Bulk Delete' : 'Delete Channel'}`,
          { isSuperUser },
          user.user,
        );
        unstable_batchedUpdates(() => {
          setIsActionPending(false);
          setSnackbarMessage(
            `Successfully updated ${
              isBulkUpdate ? `${channelIDs.length} channels` : `channel [${channelIDs[0]}]`
            }.`,
          );
        });
        onFetchChannels(uiParams);
      })
      .catch((err) => {
        unstable_batchedUpdates(() => {
          setIsActionPending(false);
          setSnackbarMessage(
            `Error updating ${
              isBulkUpdate ? `${channelIDs.length} channels` : `channel [${channelIDs[0]}]`
            }.`,
          );
          setError(err);
        });
      });
  };

  // route to create a new channel
  const navigateToNewChannelView = () => {
    track('Channels - All - Create Channel', { isSuperUser }, user.user);
    navigate(RouteChannelNew, extractRouteParams({ session, user }));
  };

  // update config for state persistence
  const updateCfg = (params: IChannelsUiParams) => {
    if (params.orderBy) cfg.defaultState.orderBy = params.orderBy;
    if (params.order) cfg.defaultState.orderDirection = params.order;
    if (params.currPage) cfg.defaultState.page = params.currPage;
    if (params.rowsPerPage) cfg.defaultState.rowsPerPage = params.rowsPerPage;
    if (params.searchInput) cfg.defaultState.searchInput = params.searchInput;
    if (params.activeColumnKeys) cfg.defaultState.activeColumnKeys = params.activeColumnKeys;
    if (params.isTableExpanded) cfg.defaultState.isTableExpanded = params.isTableExpanded;
    if (params.filters) {
      Object.values(params.filters).forEach((filter) => {
        const { menuTitle, list } = filter;
        cfg.filters[menuTitle].defaultSelected = Object.values(list).map((item) => item.ID);
      });
    }
  };

  return (
    <>
      <Channels
        deleteChannels={onDeleteChannels}
        fetchChannels={onFetchChannels}
        updateChannels={onUpdateChannels}
        cfg={cfg}
        updateCfg={updateCfg}
        navigateToNewChannelView={navigateToNewChannelView}
        isActionPending={isActionPending}
        channels={channels}
        totalRows={totalRows}
        loadState={loadState}
      />
      <SnackbarView open={!!snackbarMessage} error={error} onClose={() => setSnackbarMessage('')}>
        {snackbarMessage}
      </SnackbarView>
    </>
  );
};
